import React, { useState } from 'react';
import { arrayOf, bool, string } from 'prop-types';
import classnames from 'classnames';
import { useConfigService } from '@thd-nucleus/experience-context';
import { Checkbox, Alert, Typography, SelectionControlLabel } from '@one-thd/sui-atomic-components';
import {
  ProductPod,
  ProductImage,
  ProductRating,
  PodSection
} from '@thd-olt-component-react/product-pod';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { Price } from '@thd-olt-component-react/price';
import { getPricingInfo } from '@thd-olt-component-react/product-set';
import { QuickViewWrapper } from '../QuickViewWrapper';
import { ItemPodHeader } from './ItemPodHeader';
import { ItemPodLower } from './ItemPodLower';
import { useApplianceBundles } from '../../ApplianceBundlesProvider';
import { productShape, drawerDataShape } from '../../ApplianceBundlesShape';
import { getPricing } from '../../../utils';
import { DEFAULT_GROUP, DECLINE_MESSAGES } from '../../../constants';

function returnDesktopData({
  testId,
  itemId,
  storeId,
  isAnchor,
  heading,
  drawerData,
  isDeclinable,
  checked,
  products,
  radioValue,
  quickViewClickHandler,
  pricing,
  hasMultipleProducts,
  productGroup,
  onRadioChange,
  addItem,
  removeItem,
  setChecked
}) {
  return (
    <div
      className="sui-w-48"
      data-testid={testId}
    >
      <ProductPod
        itemId={itemId}
        storeId={storeId}
        analyticsData={{ parent: 'appliance-bundle' }}
        render={() => (
          <>
            <ItemPodHeader
              isAnchor={isAnchor}
              heading={heading}
              drawerData={drawerData}
            />
            <div className={classnames({
              'sui-bg-subtle': isAnchor
            })}
            >
              <PodSection noPadding>
                <div className="sui-px-1">
                  <SelectionControlLabel
                    alignment="center"
                    key={itemId}
                    label={!isAnchor ? 'Select' : ''}
                  >
                    <Checkbox
                      checked={!isDeclinable || checked}
                      disabled={isAnchor}
                      onChange={() => {
                        if (isDeclinable) {
                          if (checked) {
                            removeItem({ removedItem: products[radioValue] });
                          } else if (!checked) {
                            addItem({ addedItem: products[radioValue] });
                          }
                          setChecked(!checked);
                        }
                      }}
                    />
                  </SelectionControlLabel>
                </div>
              </PodSection>
              <div className="sui-pl-1 sui-pr-1 sui-pb-2">
                <PodSection columnAlign noPadding>
                  <ProductImage
                    itemId={itemId}
                    onClick={quickViewClickHandler}
                  />
                </PodSection>
                <Price
                  itemId={itemId}
                  large={false}
                  type="simple"
                  displayEachUom={false}
                  hideSavingsText
                  hideBadge
                  storeId={storeId}
                  product={pricing}
                  disableOuterSpacing
                />
                <div className="sui-px-1 sui-pb-1">
                  <div className="sui-pb-1">
                    <ProductRating
                      itemId={itemId}
                    />
                  </div>
                  <ProductHeader
                    brand="above"
                    itemId={itemId}
                    brandTitleMaxLine={3}
                  />
                </div>
                <ItemPodLower
                  hasMultipleProducts={hasMultipleProducts}
                  products={products}
                  productGroup={productGroup}
                  checked={checked}
                  radioValue={radioValue}
                  onRadioChange={onRadioChange}
                />
              </div>
            </div>
            {(!checked && !!DECLINE_MESSAGES[productGroup]) && (
              <div className="sui-pl-1 sui-pt-2 sui-w-96">
                <Alert status="warning">
                  <Typography variant="body-xs">
                    {DECLINE_MESSAGES[productGroup]}
                  </Typography>
                </Alert>
              </div>
            )}
          </>
        )}
      />
    </div>
  );
}

function returnTabletData({
  testId,
  itemId,
  storeId,
  isAnchor,
  heading,
  drawerData,
  isDeclinable,
  checked,
  removeItem,
  products,
  radioValue,
  addItem,
  setChecked,
  quickViewClickHandler,
  pricing,
  hasMultipleProducts,
  productGroup,
  onRadioChange
}) {
  return (
    <div
      className="sui-w-48"
      data-testid={testId}
    >
      <ProductPod
        itemId={itemId}
        storeId={storeId}
        render={() => (
          <>
            <ItemPodHeader
              isAnchor={isAnchor}
              heading={heading}
              drawerData={drawerData}
            />
            <div className={classnames({
              'sui-bg-subtle': isAnchor
            })}
            >
              <PodSection noPadding>
                <div className="sui-px-1">
                  <SelectionControlLabel
                    alignment="center"
                    key={itemId}
                    label={!isAnchor ? 'Select' : ''}
                  >
                    <Checkbox
                      checked={!isDeclinable || checked}
                      disabled={isAnchor}
                      onChange={() => {
                        if (isDeclinable) {
                          if (checked) {
                            removeItem({ removedItem: products[radioValue] });
                          } else if (!checked) {
                            addItem({ addedItem: products[radioValue] });
                          }
                          setChecked(!checked);
                        }
                      }}
                    />
                  </SelectionControlLabel>
                </div>
              </PodSection>
              <div className="sui-pl-1 sui-pr-1 sui-pb-2">
                <PodSection columnAlign noPadding>
                  <ProductImage
                    itemId={itemId}
                    onClick={quickViewClickHandler}
                  />
                </PodSection>
                <Price
                  itemId={itemId}
                  large={false}
                  type="simple"
                  displayEachUom={false}
                  hideSavingsText
                  hideBadge
                  storeId={storeId}
                  product={pricing}
                  disableOuterSpacing
                />
                <div className="sui-px-1 sui-pb-1">
                  <div className="sui-pb-1">
                    <ProductRating
                      itemId={itemId}
                    />
                  </div>
                  <ProductHeader
                    brand="above"
                    itemId={itemId}
                    brandTitleMaxLine={3}
                  />
                </div>
                <ItemPodLower
                  hasMultipleProducts={hasMultipleProducts}
                  products={products}
                  productGroup={productGroup}
                  checked={checked}
                  radioValue={radioValue}
                  onRadioChange={onRadioChange}
                />
              </div>
            </div>
            {(!checked && !!DECLINE_MESSAGES[productGroup]) && (
              <div className="sui-pl-1 sui-w-72 sui-h-24">
                <Alert status="warning">
                  <Typography variant="body-xs">
                    {DECLINE_MESSAGES[productGroup]}
                  </Typography>
                </Alert>
              </div>
            )}
          </>
        )}
      />
    </div>
  );
}

function returnMobileData({
  testId,
  itemId,
  storeId,
  isAnchor,
  heading,
  drawerData,
  isDeclinable,
  checked,
  removeItem,
  products,
  radioValue,
  addItem,
  setChecked,
  quickViewClickHandler,
  pricing,
  hasMultipleProducts,
  productGroup,
  onRadioChange
}) {
  return (
    <div
      data-testid={testId}
    >
      <ProductPod
        itemId={itemId}
        storeId={storeId}
        render={() => (
          <>
            {isAnchor && (
              <ItemPodHeader
                isAnchor={isAnchor}
                heading={heading}
                drawerData={drawerData}
              />
            )}
            <div className={classnames({
              'sui-bg-subtle': isAnchor
            })}
            >
              <PodSection noPadding>
                <div className="sui-px-1">
                  <SelectionControlLabel
                    alignment="center"
                    key={itemId}
                    label={!isAnchor ? 'Select' : ''}
                  >
                    <Checkbox
                      checked={!isDeclinable || checked}
                      disabled={isAnchor}
                      onChange={() => {
                        if (isDeclinable) {
                          if (checked) {
                            removeItem({ removedItem: products[radioValue] });
                          } else if (!checked) {
                            addItem({ addedItem: products[radioValue] });
                          }
                          setChecked(!checked);
                        }
                      }}
                    />
                  </SelectionControlLabel>
                </div>
              </PodSection>
              <div className="sui-flex sui-pl-1 sui-pr-1 sui-pb-2">
                <div className="sui-flex-none sui-w-32 sui-h-32 sui-mr-5">
                  <PodSection columnAlign noPadding>
                    <ProductImage
                      itemId={itemId}
                      onClick={quickViewClickHandler}
                    />
                  </PodSection>
                </div>
                <div>
                  {!isAnchor && (
                    <div>
                      <ItemPodHeader
                        isAnchor={isAnchor}
                        heading={heading}
                        drawerData={drawerData}
                      />
                    </div>
                  )}
                  <Price
                    itemId={itemId}
                    large={false}
                    type="simple"
                    displayEachUom={false}
                    hideSavingsText
                    hideBadge
                    storeId={storeId}
                    product={pricing}
                    disableOuterSpacing
                  />
                  <div className="sui-px-1 sui-pb-1">
                    <div className="sui-pb-1">
                      <ProductRating
                        itemId={itemId}
                      />
                    </div>
                    <ProductHeader
                      brand="above"
                      itemId={itemId}
                      brandTitleMaxLine={3}
                    />
                  </div>
                  <ItemPodLower
                    hasMultipleProducts={hasMultipleProducts}
                    products={products}
                    productGroup={productGroup}
                    checked={checked}
                    radioValue={radioValue}
                    onRadioChange={onRadioChange}
                  />
                </div>
              </div>
            </div>
            {(!checked && !!DECLINE_MESSAGES[productGroup]) && (
              <div className="sui-pt-2 sui-pl-1">
                <Alert status="warning">
                  <Typography variant="body-xs">
                    {DECLINE_MESSAGES[productGroup]}
                  </Typography>
                </Alert>
              </div>
            )}
          </>
        )}
      />
    </div>
  );
}

function handleRadioChange(updateItems, products, radioValue, setRadioValue) {
  return (event) => {
    const selectedValue = event.target.value;
    updateItems({
      oldItem: products[radioValue],
      newItem: products[selectedValue]
    });
    setRadioValue(selectedValue);
  };
}

function InitializeSelectedProductState(products, selectedItems, productGroup) {
  return useState(products.findIndex((product) => {
    return product.itemId === selectedItems?.find((selectedItem) => (
      selectedItem.category === productGroup
      || selectedItem.slot?.order === productGroup
      || productGroup === DEFAULT_GROUP
    ))?.itemId;
  }) || 0);
}

export const ItemPod = ({
  isAnchor,
  products,
  productGroup,
  heading,
  drawerData,
  isDeclinable,
  testId
}) => {
  const {
    storeId,
    channel,
    selectedItems,
    updateItems,
    addItem,
    removeItem,
    enableApplianceDeliveryCharge,
    enableFreeDeliveryForExchange,
    applianceDeliveryChargeValue,
    isExchangeCustomer
  } = useApplianceBundles();
  const applianceDeliveryThreshold = useConfigService('applianceDeliveryThreshold')
    ?? 396;
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [checked, setChecked] = useState(true);
  const [radioValue, setRadioValue] = InitializeSelectedProductState(products, selectedItems, productGroup);
  if (!products.length || !selectedItems) return null;
  const hasMultipleProducts = products.length > 1;

  const quickViewClickHandler = (event) => {
    event.preventDefault();
    setQuickViewOpen(true);
  };

  const onRadioChange = handleRadioChange(updateItems, products, radioValue, setRadioValue);

  const selecetdProduct = products[radioValue];
  const itemId = selecetdProduct?.product?.itemId || '';
  const { mapDetail } = getPricingInfo(
    selectedItems.map(({ product }) => product),
    enableApplianceDeliveryCharge,
    enableFreeDeliveryForExchange,
    applianceDeliveryChargeValue,
    isExchangeCustomer,
    applianceDeliveryThreshold
  );
  const pricing = getPricing([products[radioValue]], mapDetail);

  return (
    <QuickViewWrapper
      itemId={itemId}
      isQuickViewOpen={quickViewOpen}
      onQuickViewClose={() => setQuickViewOpen(false)}
    >
      {channel === 'desktop' && (
        returnDesktopData({
          testId,
          itemId,
          storeId,
          isAnchor,
          heading,
          drawerData,
          isDeclinable,
          checked,
          products,
          radioValue,
          quickViewClickHandler,
          pricing,
          hasMultipleProducts,
          productGroup,
          onRadioChange,
          addItem,
          removeItem,
          setChecked
        })
      )}
      {channel === 'tablet' && (
        returnTabletData({
          testId,
          itemId,
          storeId,
          isAnchor,
          heading,
          drawerData,
          isDeclinable,
          checked,
          removeItem,
          products,
          radioValue,
          addItem,
          setChecked,
          quickViewClickHandler,
          pricing,
          hasMultipleProducts,
          productGroup,
          onRadioChange
        })
      )}
      {channel === 'mobile' && (
        returnMobileData({
          testId,
          itemId,
          storeId,
          isAnchor,
          heading,
          drawerData,
          isDeclinable,
          checked,
          removeItem,
          products,
          radioValue,
          addItem,
          setChecked,
          quickViewClickHandler,
          pricing,
          hasMultipleProducts,
          productGroup,
          onRadioChange
        })
      )}
    </QuickViewWrapper>
  );
};

ItemPod.propTypes = {
  isAnchor: bool,
  products: arrayOf(productShape).isRequired,
  productGroup: string,
  heading: string,
  drawerData: drawerDataShape,
  isDeclinable: bool,
  testId: string
};

ItemPod.defaultProps = {
  isAnchor: false,
  productGroup: '',
  heading: '',
  drawerData: {},
  isDeclinable: false,
  testId: ''
};
